/**
 * MyReportsPage — the reporter's own support tickets (v2-A).
 *
 * Lists the in-app reports the signed-in user filed, with the reporter-visible
 * thread (admin replies + their own) and a reply box. The backend `/mine` endpoint
 * is self-scoped and sanitized (no AI triage, no internal admin notes).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, LifeBuoy, CheckCircle2 } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/TestAuthContext';
import { api } from '../utils/api';

interface ReportMessage {
  id: string;
  author?: { email?: string; role?: string };
  author_is_reporter?: boolean;
  body: string;
  created_at?: string;
}
interface MyReport {
  id: string;
  message: string;
  status?: string;
  category?: string;
  created_at?: string;
  context?: { route?: string };
  messages?: ReportMessage[];
}

const fmt = (d?: string) => {
  if (!d) return '';
  const t = new Date(d);
  return isNaN(t.getTime()) ? d : t.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function MyReportsPage() {
  const { getToken } = useAuth();
  const [reports, setReports] = useState<MyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [posting, setPosting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await api.get<{ tickets: MyReport[] }>('/api/v1/support-tickets/mine', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(data?.tickets || []);
    } catch (e: any) {
      setError(e?.message || 'Could not load your reports.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  const reply = async (id: string) => {
    const text = (drafts[id] || '').trim();
    if (!text || posting) return;
    setPosting(id);
    try {
      const token = await getToken();
      const res: any = await api.post(
        `/api/v1/support-tickets/${id}/messages`,
        { body: text, internal: false },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const msg: ReportMessage = { id: res.id, author: res.author, author_is_reporter: true, body: res.body, created_at: res.created_at };
      // Replying to a resolved report reopens it (backend flips status).
      setReports((prev) => prev.map((r) => (r.id === id ? { ...r, status: res.reopened ? 'open' : r.status, messages: [...(r.messages || []), msg] } : r)));
      setDrafts((d) => ({ ...d, [id]: '' }));
    } catch (e: any) {
      setError(e?.message || 'Could not send your reply.');
    } finally {
      setPosting(null);
    }
  };

  const empty = useMemo(() => !loading && reports.length === 0, [loading, reports]);

  return (
    <AppLayout>
      <div className="p-6 max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <LifeBuoy className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          <div>
            <h1 className="cs-display">My reports</h1>
            <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>Issues you've reported and our replies</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
          </div>
        ) : error ? (
          <p className="py-8 text-sm" style={{ color: 'var(--color-status-adverse)' }}>{error}</p>
        ) : empty ? (
          <div className="rounded-xl p-8 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              You haven't filed any reports. Use the <strong>Report</strong> button in the top bar to tell us about an issue.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((r) => {
              const resolved = (r.status || 'open') === 'resolved';
              return (
                <div key={r.id} className="rounded-xl p-5" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div style={{ color: 'var(--color-text-primary)' }}>{r.message}</div>
                    <span
                      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-mono uppercase tracking-wide shrink-0"
                      style={resolved
                        ? { color: 'var(--color-text-dim)', borderColor: 'var(--color-border)' }
                        : { color: 'var(--color-text-primary)', borderColor: 'var(--color-text-muted)' }}
                    >
                      {resolved && <CheckCircle2 className="w-3 h-3" />}
                      {r.status || 'open'}
                    </span>
                  </div>
                  <div className="font-mono text-[10px] mb-3" style={{ color: 'var(--color-text-dim)' }}>
                    {fmt(r.created_at)}{r.context?.route ? ` · ${r.context.route}` : ''}
                  </div>

                  {r.messages && r.messages.length > 0 && (
                    // Thread is subordinate to the report message: a left rule + indent,
                    // smaller/dimmer body so the reporter's own message stays loudest (K3).
                    <ul className="mb-3 pl-3" style={{ borderLeft: '2px solid var(--color-border)' }}>
                      {r.messages.map((m, mi) => (
                        <li key={m.id} className="py-2 first:pt-0" style={{ borderTop: mi === 0 ? 'none' : '1px solid var(--color-border)' }}>
                          <div className="font-mono text-[10px] mb-0.5" style={{ color: 'var(--color-text-dim)' }}>
                            {m.author_is_reporter ? 'You' : 'Support'}{m.created_at ? ` · ${fmt(m.created_at)}` : ''}
                          </div>
                          <div className="text-[13px]" style={{ color: 'var(--color-text-secondary)', opacity: 0.85 }}>{m.body}</div>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div>
                    <textarea
                      value={drafts[r.id] || ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                      rows={2}
                      placeholder="Reply…"
                      className="w-full rounded px-3 py-2 text-sm outline-none focus:outline-none resize-none"
                      style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        onClick={() => reply(r.id)}
                        disabled={!((drafts[r.id] || '').trim()) || posting === r.id}
                        className="inline-flex items-center gap-1 rounded px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed"
                        style={
                          (drafts[r.id] || '').trim() && posting !== r.id
                            ? { background: 'var(--color-brand)', color: 'var(--color-on-accent)' }
                            : { background: 'transparent', border: '1px solid var(--color-border)', color: 'var(--color-text-dim)', opacity: 0.6 }
                        }
                      >
                        {posting === r.id ? 'Sending…' : 'Reply'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
